import Container from '../../../atoms/container/adsContainer';
import DateUtils from '../../../../../shared/utils/date';
import Focus from '../../../../../shared/types/enums/focus';
import Promo from '../../../atoms/text/adsPromoText';
import Remainder from '../../../atoms/text/AdsRemainderText';
import Spinner from '../../spinner/spinner';
import TranslationManager from '../../../../../shared/assets/locale/translationsManager';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  adsSelector,
  clearVideoAd,
  setImpression,
} from '../../../../../shared/store/slices/ads.slice';
import {
  focusSelector,
  popFocus,
  pushFocus,
} from '../../../../../shared/store/slices/focus.slice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../shared/store/hooks';

const AD_TIMEOUT = 30 * 1000;
const OPACITY_TIMEOUT = 0.5 * 1000;

function IMAHandler() {
  const dispatch = useAppDispatch();
  const { videoAd: source } = useAppSelector(adsSelector);
  const { focus } = useAppSelector(focusSelector);
  const [loaded, setLoaded] = useState(false);
  const [remainder, setRemainder] = useState('');
  const [show, setShow] = useState(true);

  // HTML elements
  const ads = useRef<HTMLDivElement | null>(null);
  const video = useRef<HTMLVideoElement | null>(null);

  // Ad refs
  const focusRef = useRef(focus);
  const src = useRef<string | undefined>(source);
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  focusRef.current = focus;

  // IMA objects
  // eslint-disable-next-line no-use-before-define
  const container = useRef<any>(null);
  // eslint-disable-next-line no-use-before-define
  const loader = useRef<any>(null);
  // eslint-disable-next-line no-use-before-define
  const manager = useRef<any>(null);
  // eslint-disable-next-line no-use-before-define
  const request = useRef<any>(null);

  // Listeners

  const onHide = useCallback(() => {
    dispatch(popFocus());
    setShow(false);
    setTimeout(() => {
      dispatch(clearVideoAd());
    }, OPACITY_TIMEOUT);
  }, []);

  const onEnded = useCallback(() => {
    onHide();
    loader.current?.contentComplete();
    dispatch(setImpression(Date.now()));
  }, []);

  const onError = useCallback((error?: string) => {
    onHide();
    error && console.error(error);
    manager.current?.destroy();
    return false;
  }, []);

  const onLoaded = useCallback(
    (event: { getAd: () => { isLinear: () => boolean } }) => {
      !event?.getAd()?.isLinear() && video.current?.play();

      // Ad timeout
      clearTimeout(timeout.current);
      timeout.current = setTimeout(() => {
        setLoaded((prev) => {
          !prev && onError('[IMA] Ad response timeout');
          return prev;
        });
      }, AD_TIMEOUT);
    },
    []
  );

  const onProgress = useCallback(
    (event: {
      getAdData: () => { duration?: number; currentTime?: number };
    }) => {
      const progress = event?.getAdData();
      const duration = (progress?.duration || 0) * 1000;
      const position = (progress?.currentTime || 0) * 1000;
      const remainder = DateUtils.getOffset(position, duration);
      if (duration > 0) {
        const text = `${String(remainder.minutes).padStart(2, '0')}:${String(
          remainder.seconds
        ).padStart(2, '0')}`;
        setRemainder(TranslationManager.get('COMPONENTS.ADS.REMAINDER', text));
      } else {
        setRemainder('');
      }
    },
    []
  );

  const onStart = useCallback(() => {
    setLoaded(true);
  }, []);

  // Initializers

  const initializeIMA = useCallback(() => {
    container.current = new google.ima.AdDisplayContainer(
      ads.current,
      video.current
    );
    loader.current = new google.ima.AdsLoader(container.current);
    request.current = new google.ima.AdsRequest();

    initializeListeners();
    initializeRequest();
  }, []);

  const initializeListeners = useCallback(() => {
    if (!loader.current) {
      onError('[IMA] Initialize listeners: Ads loader not ready');
      return;
    }

    // Ad manager load
    loader.current?.addEventListener(
      google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      initializeManager,
      false
    );

    // Ad error listener
    loader.current?.addEventListener(
      google.ima.AdErrorEvent.Type.AD_ERROR,
      (error: any) => onError(error?.getError()),
      false
    );
  }, []);

  const initializeManager = useCallback(
    (event: any) => {
      if (!loader.current) {
        onError('[IMA] Initialize manager: Ads loader not ready');
        return;
      }

      // Instantiate ads manager
      manager.current = event?.getAdsManager(video.current);

      // Resize ads manager
      const width = video.current?.clientWidth;
      const height = video.current?.clientHeight;
      manager.current?.resize(width, height, google.ima.ViewMode.NORMAL);

      // Ad load listener
      manager.current?.addEventListener(
        google.ima.AdEvent.Type.LOADED,
        onLoaded
      );

      // Ad start listener
      manager.current?.addEventListener(
        google.ima.AdEvent.Type.STARTED,
        onStart
      );

      // Ad complete listener
      manager.current?.addEventListener(
        google.ima.AdEvent.Type.COMPLETE,
        onEnded
      );

      // Ad close listener
      manager.current?.addEventListener(
        google.ima.AdEvent.Type.USER_CLOSE,
        onEnded
      );

      // Ad skipped listener
      manager.current?.addEventListener(
        google.ima.AdEvent.Type.SKIPPED,
        onEnded
      );

      // Ad progress listener
      manager.current?.addEventListener(
        google.ima.AdEvent.Type.AD_PROGRESS,
        onProgress
      );

      // Ad error listener
      manager.current?.addEventListener(
        google.ima.AdErrorEvent.Type.AD_ERROR,
        (error: any) => onError(error?.getError())
      );

      // Load ads
      if (!loaded) {
        try {
          // Initialize ad container
          video.current?.load();
          container.current?.initialize();

          // Initialize ad manager
          const width = video.current?.clientWidth || 0;
          const height = video.current?.clientHeight || 0;
          manager.current?.init(width, height, google.ima.ViewMode.NORMAL);
          manager.current?.start();
        } catch (error) {
          onError('[IMA] Ads manager initialization error');
        }
      }
    },
    [video]
  );

  const initializeRequest = useCallback(() => {
    if (!request.current) {
      onError('[IMA] Initialize request: Ads request handler not ready');
      return;
    }
    if (!src.current) {
      onError('[IMA] Initialize request: Source not found');
      return;
    }

    // Specify the ad url
    request.current.adTagUrl = src.current;

    // Specify the linear and nonlinear slot sizes
    request.current.linearAdSlotWidth = video.current?.clientWidth || 0;
    request.current.linearAdSlotHeight = video.current?.clientHeight || 0;
    request.current.nonLinearAdSlotWidth = video.current?.clientWidth || 0;
    request.current.nonLinearAdSlotHeight =
      (video.current?.clientHeight || 0) / 3;

    // Pass the request to the loader.current to request ads
    loader.current?.requestAds(request.current);
  }, []);

  // Effect hooks

  useEffect(() => {
    if (!ads.current || !video.current || !source) {
      !ads.current && console.error('[IMA] Ads container not found');
      !video.current && console.error('[IMA] Video element not found');
      !source && console.error('[IMA] Source not found');
      onError();
    } else {
      try {
        initializeIMA();
        dispatch(pushFocus(Focus.ADS));
      } catch (error) {
        onError('[IMA] Load error');
      }
    }
    return () => {
      clearTimeout(timeout.current);
      focusRef.current === Focus.ADS && dispatch(popFocus());
    };
  }, []);

  useEffect(() => {
    if (!request.current) {
      onError('[IMA] Source update: Ads request handler not ready');
      return;
    }
    if (!source) {
      onError('[IMA] Source update: No source found');
      return;
    }
    if (src.current !== source) {
      try {
        src.current = source;
        initializeRequest();
      } catch (error) {
        onError('[IMA] Source update error');
      }
    }
  }, [source]);

  return (
    <Container className={show ? 'video-ads-fade-in' : 'video-ads-fade-out'}>
      <>
        <video id={'video-element'} ref={video} />
        <div id={'ad-container'} ref={ads} />
      </>
      <>
        <Promo>{TranslationManager.get('COMPONENTS.ADS.PROMO')}</Promo>
        {remainder ? <Remainder>{remainder}</Remainder> : null}
        {!loaded && <Spinner />}
      </>
    </Container>
  );
}

export default IMAHandler;
